import { memo } from 'react';
import { Box, FormField, Link, Select, SpaceBetween, Textarea } from '@amzn/awsui-components-core';
import { ContentQualityResponseOptions } from '@amzn/awscat-pattern-library-service-model';

import { BarRaiserQuestionItemViewModel } from './BarRaiserEvaluationPanelViewModel';
import styles from './BarRaiserEvaluationPanel.module.scss';

interface Props {
  readonly question: BarRaiserQuestionItemViewModel;
}

/** Renders the question body with any embedded hyperlinks as external links. */
function renderQuestionBody(question: BarRaiserQuestionItemViewModel) {
  const { questionBody, hyperlinks } = question;
  if (!hyperlinks || hyperlinks.links.length === 0) {
    return questionBody;
  }

  // Splice each linked word into the body, in the order it appears.
  const sorted = hyperlinks.links
    .map((link) => ({ ...link, index: questionBody.indexOf(link.word) }))
    .filter((link) => link.index >= 0)
    .sort((a, b) => a.index - b.index);

  const segments: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  sorted.forEach(({ word, link, index }) => {
    segments.push(<span key={key++}>{questionBody.substring(cursor, index)}</span>);
    segments.push(
      <Link key={key++} external href={link}>
        {word}
      </Link>,
    );
    cursor = index + word.length;
  });
  segments.push(<span key={key}>{questionBody.substring(cursor)}</span>);
  return segments;
}

/** One questionnaire row: question text, Yes/No/N/A select, and a comment box on "No". */
export const BarRaiserEvaluationQuestionItem = memo(function BarRaiserEvaluationQuestionItem({
  question,
}: Props) {
  return (
    <div className={styles.questionItem}>
      <SpaceBetween size="xs" direction="vertical">
        <div>
          {question.displaySequence} {renderQuestionBody(question)}
        </div>
        {question.note && <Box variant="small">{question.note}</Box>}

        <Select
          selectedOption={question.selectedOption}
          options={question.responseOptions}
          placeholder="Select"
          disabled={question.isReadOnly}
          ariaLabel={`Answer for question ${question.displaySequence}`}
          onChange={({ detail }) =>
            question.onResponseChange(detail.selectedOption.value as ContentQualityResponseOptions)
          }
        />

        {question.showCommentBox && (
          <FormField
            constraintText={
              <Box color="text-body-secondary">
                {question.commentCharsRemaining} characters remaining
              </Box>
            }
          >
            <Textarea
              value={question.comment}
              placeholder="Enter comments - optional"
              rows={3}
              disabled={question.isReadOnly}
              nativeTextareaAttributes={{ maxLength: question.commentMaxLength }}
              onChange={({ detail }) => question.onCommentChange(detail.value)}
            />
          </FormField>
        )}
      </SpaceBetween>
    </div>
  );
});
